import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Palette, Upload, Globe, Check, AlertCircle, Loader2,
  Smartphone, Monitor, Copy, ExternalLink, RefreshCw,
  Image as ImageIcon, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('session_token')}`
});

const PWABrandingConfig = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [branding, setBranding] = useState({
    name: '',
    short_name: '',
    description: '',
    theme_color: '#4F46E5',
    background_color: '#FFFFFF',
    text_color: '#1E293B',
    accent_color: '#10B981'
  });
  const [domain, setDomain] = useState('');
  const [isSubdomain, setIsSubdomain] = useState(true);
  const [domainStatus, setDomainStatus] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(null);
  const [previewMode, setPreviewMode] = useState('mobile');

  useEffect(() => {
    loadConfig();
    loadDomainStatus();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await axios.get(`${API}/pwa/config`, { headers: getAuthHeaders() });
      setConfig(res.data);
      if (res.data.branding) {
        setBranding(prev => ({ ...prev, ...res.data.branding }));
      }
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDomainStatus = async () => {
    try {
      const res = await axios.get(`${API}/pwa/domain/status`, { headers: getAuthHeaders() });
      setDomainStatus(res.data);
      if (res.data.domain) {
        setDomain(res.data.is_subdomain 
          ? res.data.domain.replace('.tuplazaia.com', '')
          : res.data.domain
        );
        setIsSubdomain(res.data.is_subdomain);
      }
    } catch (error) {
      console.error('Error loading domain status:', error);
    }
  };

  const handleSaveBranding = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/pwa/config`, branding, { headers: getAuthHeaders() });
      toast.success('Branding guardado correctamente');
      loadConfig();
    } catch (error) {
      toast.error('Error al guardar el branding');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadLogo = async (e, size) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(size);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(
        `${API}/pwa/upload-logo?size=${size}`,
        formData,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`Logo ${size}x${size} subido correctamente`);
      setBranding(prev => ({ ...prev, [`logo_${size}`]: res.data.logo_url }));
      loadConfig();
    } catch (error) {
      toast.error('Error al subir el logo');
    } finally {
      setUploadingLogo(null);
    }
  };

  const handleConfigureDomain = async () => {
    if (!domain.trim()) {
      toast.error('Introduce un dominio');
      return;
    }

    try {
      const res = await axios.post(`${API}/pwa/domain`, {
        domain: domain.trim(),
        is_subdomain: isSubdomain
      }, { headers: getAuthHeaders() });

      toast.success('Dominio configurado');
      setDomainStatus(res.data);
      
      if (res.data.dns_instructions) {
        // Show DNS instructions
        toast.info('Configura el DNS según las instrucciones', { duration: 5000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al configurar dominio');
    }
  };

  const handleRemoveDomain = async () => {
    if (!confirm('¿Seguro que quieres eliminar el dominio?')) return;

    try {
      await axios.delete(`${API}/pwa/domain`, { headers: getAuthHeaders() });
      toast.success('Dominio eliminado');
      setDomainStatus(null);
      setDomain('');
    } catch (error) {
      toast.error('Error al eliminar dominio');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Smartphone className="w-6 h-6 text-indigo-600" />
          Configuración PWA White-Label
        </h2>
        <p className="text-slate-600 mt-1">
          Personaliza tu aplicación con tu marca y dominio
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Configuración */}
        <div className="space-y-6">
          {/* Branding Colors */}
          <div className="bg-white rounded-xl border p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Palette className="w-5 h-5 text-indigo-600" />
              Colores y Texto
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nombre de la App
                </label>
                <Input
                  value={branding.name}
                  onChange={(e) => setBranding({ ...branding, name: e.target.value })}
                  placeholder="Mi Academia"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nombre Corto (para icono)
                </label>
                <Input
                  value={branding.short_name}
                  onChange={(e) => setBranding({ ...branding, short_name: e.target.value.slice(0, 12) })}
                  placeholder="MiApp"
                  maxLength={12}
                />
                <p className="text-xs text-slate-500 mt-1">Máximo 12 caracteres</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Descripción
                </label>
                <Input
                  value={branding.description}
                  onChange={(e) => setBranding({ ...branding, description: e.target.value })}
                  placeholder="Plataforma de formación online"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Color Principal
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={branding.theme_color}
                      onChange={(e) => setBranding({ ...branding, theme_color: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={branding.theme_color}
                      onChange={(e) => setBranding({ ...branding, theme_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Color de Fondo
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={branding.background_color}
                      onChange={(e) => setBranding({ ...branding, background_color: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={branding.background_color}
                      onChange={(e) => setBranding({ ...branding, background_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Color de Texto
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={branding.text_color}
                      onChange={(e) => setBranding({ ...branding, text_color: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={branding.text_color}
                      onChange={(e) => setBranding({ ...branding, text_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Color de Acento
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={branding.accent_color}
                      onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={branding.accent_color}
                      onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveBranding} disabled={saving} className="w-full">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Guardar Colores
              </Button>
            </div>
          </div>

          {/* Logos */}
          <div className="bg-white rounded-xl border p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-indigo-600" />
              Logos
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Logo 192 */}
              <div className="border-2 border-dashed rounded-xl p-4 text-center">
                <p className="text-sm font-medium mb-2">Logo 192x192</p>
                {branding.logo_192 && !branding.logo_192.includes('favicon') ? (
                  <img
                    src={branding.logo_192.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_192}` : branding.logo_192}
                    alt="Logo 192"
                    className="w-24 h-24 mx-auto mb-2 object-contain"
                  />
                ) : (
                  <div className="w-24 h-24 mx-auto mb-2 bg-slate-100 rounded-xl flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-slate-400" />
                  </div>
                )}
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleUploadLogo(e, '192')}
                    className="hidden"
                  />
                  <Button variant="outline" size="sm" asChild disabled={uploadingLogo === '192'}>
                    <span>
                      {uploadingLogo === '192' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-1" />
                      )}
                      Subir
                    </span>
                  </Button>
                </label>
              </div>

              {/* Logo 512 */}
              <div className="border-2 border-dashed rounded-xl p-4 text-center">
                <p className="text-sm font-medium mb-2">Logo 512x512</p>
                {branding.logo_512 && !branding.logo_512.includes('favicon') ? (
                  <img
                    src={branding.logo_512.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_512}` : branding.logo_512}
                    alt="Logo 512"
                    className="w-24 h-24 mx-auto mb-2 object-contain"
                  />
                ) : (
                  <div className="w-24 h-24 mx-auto mb-2 bg-slate-100 rounded-xl flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-slate-400" />
                  </div>
                )}
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleUploadLogo(e, '512')}
                    className="hidden"
                  />
                  <Button variant="outline" size="sm" asChild disabled={uploadingLogo === '512'}>
                    <span>
                      {uploadingLogo === '512' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-1" />
                      )}
                      Subir
                    </span>
                  </Button>
                </label>
              </div>
            </div>

            <p className="text-xs text-slate-500 mt-4">
              Recomendado: PNG con fondo transparente. El logo 512x512 se usa como splash screen.
            </p>
          </div>

          {/* Dominio */}
          <div className="bg-white rounded-xl border p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-indigo-600" />
              Dominio
            </h3>

            {domainStatus?.domain ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">{domainStatus.domain}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {domainStatus.verified ? (
                        <span className="flex items-center gap-1 text-xs text-green-600">
                          <Check className="w-3 h-3" /> Verificado
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-amber-600">
                          <AlertCircle className="w-3 h-3" /> Pendiente
                        </span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        domainStatus.ssl_status === 'active' 
                          ? 'bg-green-100 text-green-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        SSL: {domainStatus.ssl_status}
                      </span>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleRemoveDomain}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                {!domainStatus.verified && !domainStatus.is_subdomain && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="font-medium text-amber-800 mb-2">Configura tu DNS:</p>
                    <div className="bg-white rounded p-3 font-mono text-sm">
                      <p>Tipo: <strong>CNAME</strong></p>
                      <p>Nombre: <strong>{domainStatus.domain.split('.')[0]}</strong></p>
                      <p>Valor: <strong>app.tuplazaia.com</strong></p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => copyToClipboard('app.tuplazaia.com')}
                    >
                      <Copy className="w-4 h-4 mr-1" /> Copiar valor
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsSubdomain(true)}
                    className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                      isSubdomain 
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <p className="font-medium">Subdominio gratuito</p>
                    <p className="text-xs text-slate-500">academia.tuplazaia.com</p>
                  </button>
                  <button
                    onClick={() => setIsSubdomain(false)}
                    className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                      !isSubdomain 
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <p className="font-medium">Dominio propio</p>
                    <p className="text-xs text-slate-500">app.tudominio.com</p>
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {isSubdomain ? 'Nombre del subdominio' : 'Tu dominio'}
                  </label>
                  <div className="flex">
                    <Input
                      value={domain}
                      onChange={(e) => setDomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      placeholder={isSubdomain ? 'mi-academia' : 'app.midominio.com'}
                      className={isSubdomain ? 'rounded-r-none' : ''}
                    />
                    {isSubdomain && (
                      <span className="px-3 py-2 bg-slate-100 border border-l-0 rounded-r-lg text-slate-600 text-sm">
                        .tuplazaia.com
                      </span>
                    )}
                  </div>
                </div>

                <Button onClick={handleConfigureDomain} className="w-full">
                  <Globe className="w-4 h-4 mr-2" />
                  Configurar Dominio
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Preview */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-lg">Vista Previa</h3>
            <div className="flex gap-2">
              <Button
                variant={previewMode === 'mobile' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPreviewMode('mobile')}
              >
                <Smartphone className="w-4 h-4" />
              </Button>
              <Button
                variant={previewMode === 'desktop' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPreviewMode('desktop')}
              >
                <Monitor className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Phone Mockup */}
          <div className="flex justify-center">
            <div className={`relative ${previewMode === 'mobile' ? 'w-72' : 'w-full max-w-lg'}`}>
              <div
                className={`bg-slate-900 rounded-[2.5rem] p-3 shadow-2xl ${
                  previewMode === 'mobile' ? '' : 'rounded-2xl'
                }`}
              >
                {previewMode === 'mobile' && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-slate-900 rounded-b-2xl" />
                )}
                <div
                  className="rounded-[2rem] overflow-hidden"
                  style={{ backgroundColor: branding.background_color }}
                >
                  {/* Status Bar */}
                  <div
                    className="h-8 flex items-center justify-between px-6"
                    style={{ backgroundColor: branding.theme_color }}
                  >
                    <span className="text-white text-xs">9:41</span>
                    <div className="flex gap-1">
                      <div className="w-4 h-2 bg-white/80 rounded-sm" />
                      <div className="w-4 h-2 bg-white/80 rounded-sm" />
                    </div>
                  </div>

                  {/* App Header */}
                  <div
                    className="p-4 flex items-center gap-3"
                    style={{ backgroundColor: branding.theme_color }}
                  >
                    {branding.logo_192 && !branding.logo_192.includes('favicon') ? (
                      <img
                        src={branding.logo_192.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_192}` : branding.logo_192}
                        alt="Logo"
                        className="w-10 h-10 rounded-xl bg-white/20"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                        <span className="text-white font-bold text-lg">
                          {(branding.short_name || branding.name || 'A')[0]}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="text-white font-bold">{branding.name || 'Mi Academia'}</p>
                      <p className="text-white/70 text-xs">
                        {domainStatus?.domain || 'academia.tuplazaia.com'}
                      </p>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 space-y-3" style={{ minHeight: previewMode === 'mobile' ? '400px' : '300px' }}>
                    <div
                      className="p-4 rounded-xl"
                      style={{ backgroundColor: `${branding.theme_color}15` }}
                    >
                      <p style={{ color: branding.text_color }} className="font-medium">
                        Bienvenido a {branding.name || 'tu academia'}
                      </p>
                      <p style={{ color: branding.text_color }} className="text-sm opacity-70">
                        {branding.description || 'Tu plataforma de formación'}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div
                        className="p-3 rounded-xl text-center"
                        style={{ backgroundColor: branding.theme_color, color: '#fff' }}
                      >
                        <p className="font-bold text-lg">125</p>
                        <p className="text-xs opacity-80">Preguntas</p>
                      </div>
                      <div
                        className="p-3 rounded-xl text-center"
                        style={{ backgroundColor: branding.accent_color, color: '#fff' }}
                      >
                        <p className="font-bold text-lg">85%</p>
                        <p className="text-xs opacity-80">Aciertos</p>
                      </div>
                    </div>

                    <button
                      className="w-full py-3 rounded-xl font-medium"
                      style={{
                        backgroundColor: branding.theme_color,
                        color: '#fff'
                      }}
                    >
                      Comenzar Test
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Install Instructions */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
            <h4 className="font-medium text-indigo-900 mb-2">Cómo instalar la PWA</h4>
            <ol className="text-sm text-indigo-700 space-y-1">
              <li>1. Accede a <strong>{domainStatus?.domain || 'tu-academia.tuplazaia.com'}</strong></li>
              <li>2. En el navegador, toca "Añadir a pantalla de inicio"</li>
              <li>3. ¡La app aparecerá como una aplicación nativa!</li>
            </ol>
            {domainStatus?.domain && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => window.open(`https://${domainStatus.domain}`, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Abrir PWA
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWABrandingConfig;
